import { useEffect, useId, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import mermaid from "mermaid";
import { resolveImage } from "../docs";

mermaid.initialize({
  startOnLoad: false,
  theme: "neutral",
  securityLevel: "loose",
  flowchart: { curve: "basis", htmlLabels: true },
  themeVariables: { fontFamily: "system-ui, -apple-system, Segoe UI, Roboto, sans-serif", fontSize: "13px" },
});

/** A Mermaid diagram rendered to inline SVG; the source stays available for print fallbacks. */
export function Mermaid({ chart }) {
  const id = useId().replace(/[^a-zA-Z0-9]/g, "");
  const ref = useRef(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setError(null);
    mermaid
      .render(`m${id}`, chart)
      .then(({ svg, bindFunctions }) => {
        if (cancelled || !ref.current) return;
        ref.current.innerHTML = svg;
        bindFunctions?.(ref.current);
      })
      .catch((e) => {
        if (!cancelled) setError(String(e?.message || e));
      });
    return () => {
      cancelled = true;
    };
  }, [chart, id]);
  if (error) {
    return (
      <pre className="overflow-x-auto rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
        {`diagram error: ${error}\n\n${chart}`}
      </pre>
    );
  }
  return <div ref={ref} className="mermaid-figure my-4 flex justify-center overflow-x-auto" />;
}

export function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[`*_]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");
}

function textOf(children) {
  if (typeof children === "string") return children;
  if (Array.isArray(children)) return children.map(textOf).join("");
  if (children && typeof children === "object" && "props" in children) return textOf(children.props.children);
  return "";
}

function heading(Tag) {
  return function Heading({ children, ...rest }) {
    const id = slugify(textOf(children));
    return (
      <Tag id={id} {...rest}>
        <a href={`#${id}`} className="no-underline hover:underline">
          {children}
        </a>
      </Tag>
    );
  };
}

const components = {
  h1: heading("h1"),
  h2: heading("h2"),
  h3: heading("h3"),
  h4: heading("h4"),
  img: ({ src, alt, ...rest }) => <img src={resolveImage(src)} alt={alt || ""} loading="lazy" {...rest} />,
  a: ({ href, children, ...rest }) => {
    const external = /^https?:/.test(href || "");
    return (
      <a href={href} target={external ? "_blank" : undefined} rel={external ? "noreferrer" : undefined} {...rest}>
        {children}
      </a>
    );
  },
  code: ({ className, children, ...rest }) => {
    const lang = /language-(\w+)/.exec(className || "")?.[1];
    if (lang === "mermaid") return <Mermaid chart={String(children).replace(/\n$/, "")} />;
    if (!className && !String(children).includes("\n")) {
      return (
        <code className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em]" {...rest}>
          {children}
        </code>
      );
    }
    return (
      <code className={className} {...rest}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => {
    // a mermaid block renders its own figure; everything else is a code block
    const child = Array.isArray(children) ? children[0] : children;
    if (child?.props?.className === "language-mermaid") return <>{children}</>;
    return <pre className="overflow-x-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">{children}</pre>;
  },
  table: ({ children }) => (
    <div className="my-4 overflow-x-auto">
      <table className="min-w-full border-collapse text-sm">{children}</table>
    </div>
  ),
};

/** Renders a documentation chapter: GitHub-flavoured Markdown, Mermaid diagrams, anchored headings, bundled images. */
export function Markdown({ text }) {
  return (
    <div className="prose-docs">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {text}
      </ReactMarkdown>
    </div>
  );
}

/** The `##`/`###` headings of a chapter, for an "On this page" list. */
export function outlineOf(text) {
  const out = [];
  let inFence = false;
  for (const line of (text || "").split("\n")) {
    if (/^```/.test(line)) inFence = !inFence;
    if (inFence) continue;
    const m = /^(##|###)\s+(.+)$/.exec(line);
    if (m) out.push({ level: m[1].length, title: m[2].replace(/[`*_]/g, "").trim(), id: slugify(m[2]) });
  }
  return out;
}
